import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { formatPrice } from "../../functions/utils";
import Stepper from "../Stepper/Stepper";
import InputForm from "../InputForm/InputForm";
import SubmitButton from "../Button/SubmitButton";
import config from "../../configuration.json";
import { TranslateContext } from "../../App";
import { ConfigContext } from "../../App";
import Banner from "../Banner/Banner";

/**
 * Checkout items component displaying cart contents and order form.
 * @param {Object} props - Component props.
 * @param {Array} props.cartItems - Items in the cart.
 * @param {Function} props.onAdd - Handler for adding item quantity.
 * @param {Function} props.onSubtract - Handler for subtracting item quantity.
 * @param {number} props.totalPrice - Total price of items.
 * @param {number} props.shippingPrice - Shipping cost.
 * @param {Function} props.handleSubmit - Form submit handler from react-hook-form.
 * @param {Function} props.handleFormSubmit - Custom form submission handler.
 * @param {Object} props.formRef - Reference to the form element.
 * @param {Function} props.register - Register function from react-hook-form.
 * @param {Function} props.setValue - setValue from react-hook-form.
 * @param {Object} props.errors - Form validation errors.
 * @param {boolean} props.isMobile - Whether the view is mobile.
 * @param {Array<Object>} props.shippingOptions - Normalized shipping carriers.
 * @param {Object} props.selectedCarrier - Selected carrier option.
 * @param {Function} props.setSelectedCarrier - Carrier change handler.
 * @param {string} props.destinationType - home|office|box.
 * @param {Function} props.setDestinationType - Destination type handler.
 * @param {Object|null} props.selectedOffice - Speedy office payload.
 * @param {Function} props.setSelectedOffice - Office selection handler.
 * @param {string} props.selectedDelivery - Display label for selected carrier.
 * @param {boolean} props.isProcessing - Whether the checkout is being processed.
 */
function CheckoutItems({
  cartItems,
  onAdd,
  onSubtract,
  totalPrice,
  shippingPrice,
  handleSubmit,
  handleFormSubmit,
  formRef,
  register,
  setValue,
  errors,
  isMobile,
  shippingOptions,
  selectedCarrier,
  setSelectedCarrier,
  destinationType,
  setDestinationType,
  selectedOffice,
  setSelectedOffice,
  selectedDelivery,
  isProcessing = false,
}) {
  const language = useContext(TranslateContext).language;
  const configuration = useContext(ConfigContext);

  const destinationLabel =
    destinationType === "home"
      ? config.texts.destinationHome?.[language]
      : destinationType === "office"
        ? config.texts.destinationOffice?.[language]
        : config.texts.destinationBox?.[language];

  return (
    <div className="checkout">
      {configuration?.checkoutBanner?.show && (
        <div className="row bg-[#f8fafe]">
          <div className="row-container">
            <Banner
              type={configuration?.checkoutBanner?.type || "warning"}
              message={configuration?.checkoutBanner?.message}
            />
          </div>
        </div>
      )}
      <div className="row">
        <div className="checkout-product-list">
          <div className="checkout-product-list-header-row">
            <p></p>
            <p>{config.texts.product[language]}</p>
            {!isMobile && <p>{config.texts.price[language]}</p>}
            <p>{config.texts.quantity[language]}</p>
            <p>{config.texts.total[language]}</p>
          </div>
          <hr />
          {cartItems.map((item, index) => (
            <React.Fragment key={index}>
              <div className="checkout-product-list-item" key={index}>
                <img className="checkout-product-list-item-image" src={item.image.src} alt={item.image.alt} />
                <Link to={item.url} className="checkout-product-list-item-title">
                  {item.title[language] ? item.title[language] : item.title}
                </Link>
                {!isMobile && (
                  <div className="checkout-product-list-item-unit-price">
                    <p>{formatPrice(item.price, language, config)}</p>
                  </div>
                )}
                <Stepper
                  key={item.id}
                  itemId={item.id}
                  disableAdd={item.cartQuantity === item.quantity}
                  onAdd={() => {
                    onAdd(item.id);
                  }}
                  onSubtract={() => {
                    onSubtract(item.id);
                  }}
                />
                <div className="checkout-product-list-item-total">
                  <p>{formatPrice(item.price * item.cartQuantity, language, config)}</p>
                </div>
              </div>
              <hr />
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="row">
        <div className="checkout-contact-summary-container">
          <div className="w-full flex flex-col gap-10 lg:w-1/2">
            <p className="checkout-user-input-form-title">{config.texts.shippingDetails[language]}</p>
            <InputForm
              register={register}
              setValue={setValue}
              errors={errors}
              shippingOptions={shippingOptions}
              selectedCarrier={selectedCarrier}
              setSelectedCarrier={setSelectedCarrier}
              destinationType={destinationType}
              setDestinationType={setDestinationType}
              selectedOffice={selectedOffice}
              setSelectedOffice={setSelectedOffice}
              onSubmit={handleSubmit(handleFormSubmit)}
            />
          </div>
          <div className="checkout-v-divider" />
          <div className="checkout-summary">
            <div className="checkout-summary-card">
              <p>{config.texts.productsCost[language]}</p>
              <div className="checkout-summary-price">
                <span>{formatPrice(totalPrice, language, config)}</span>
              </div>
              <p className="checkout-summary-title">{config.texts.shippingCost[language]}</p>
              <div className="checkout-summary-title">
                {shippingPrice ? (
                  <div className="checkout-summary-price">
                    <span>{formatPrice(shippingPrice, language, config)}</span>
                  </div>
                ) : (
                  <span>
                    {selectedDelivery}
                    {destinationLabel ? ` · ${destinationLabel}` : ""}
                  </span>
                )}
              </div>
              <hr />
              <hr />
              <p className="checkout-summary-title-bold">
                {config.texts.totalCost[language]}
                <p className="w-full text-xs">{config.texts.noShippingPriceAdded[language]}</p>
              </p>
              <div className="checkout-summary-title-bold">
                <span>
                  {formatPrice(
                    parseFloat(totalPrice) + Number(shippingPrice ? shippingPrice : ""),
                    language,
                    config
                  )}
                </span>
              </div>
              <p>{config.texts.paymentMethod[language]}</p>
              <span className="font-bold flex items-start">{config.texts.onSitePayment[language]}</span>
            </div>
            <div className="checkout-terms-check">
              <label className="checkout-terms-check-label">
                <input
                  className="checkout-terms-check-input"
                  type="checkbox"
                  {...register("termsAndConditions", { required: true })}
                />
                <p className="w-full">
                  <span className="font-bold">{config.texts.shippingTermsText[language]}</span>{" "}
                  {config.texts.readAndAgreeWith[language]}{" "}
                  <a
                    className="italic underline"
                    href="/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {config.texts.termsAndConditions[language]}
                  </a>{" "}
                  и{" "}
                  <a className="italic underline" href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                    {config.texts.privacyPolicy[language]}
                  </a>
                  .
                </p>
              </label>{" "}
              {errors.termsAndConditions && (
                <span className="checkout-terms-error">{config.texts.pleaseAgreeWithError[language]}</span>
              )}
              <SubmitButton type={"large"} onClick={handleSubmit(handleFormSubmit)} isLoading={isProcessing} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutItems;
